import styled from 'styled-components';
import { Button } from '../../../components/Button';
import { useFindCodeLink } from '../../subscription/hooks/useFindCodeLink';

type CodeTypeProps = {
  onClick: () => void;
  onGoBack: () => void;
  codeType: string;
  setCodeType: (codeType: string) => void;
  isActive: boolean;
};

export const CodeType = ({ onClick, codeType, setCodeType, isActive, onGoBack }: CodeTypeProps) => {
  const { LinkButton, HelperModalComponent } = useFindCodeLink();

  const handleWrapperClick = (value: string) => {
    setCodeType(value);
  };

  return (
    <Container>
      <Title>Activation code type</Title>
      <RadioContainer>
        <RadioWrapper 
          data-selected={codeType === '4-digits'} 
          onClick={() => handleWrapperClick('4-digits')}
        >
          <Radio
            type="radio"
            name="code-type"
            value="4-digits"
            checked={codeType === '4-digits'}
            onChange={(e) => setCodeType(e.target.value)}
            id="radio-4digit"
          />
          <Label htmlFor="radio-4digit">
            4-digit code
            <Example>Example: ABCD</Example>
          </Label>
        </RadioWrapper>
        <RadioWrapper 
          data-selected={codeType === '20-digits'} 
          onClick={() => handleWrapperClick('20-digits')}
        >
          <Radio
            type="radio"
            name="code-type"
            value="20-digits"
            checked={codeType === '20-digits'}
            onChange={(e) => setCodeType(e.target.value)}
            id="radio-20digit"
          />
          <Label htmlFor="radio-20digit">
            ICCID SIM
            <Example>Example: 89123456789098765432</Example>
          </Label>
        </RadioWrapper>
      </RadioContainer>
      {LinkButton}
      <ButtonWrapper>
        <Button onClick={onGoBack}>Back</Button>
        <Button onClick={onClick} disabled={!isActive}>
          Next
        </Button>
      </ButtonWrapper>
      {HelperModalComponent}
    </Container>
  );
};

// Add this to the component style to make the dot appear
const style = document.createElement('style');
style.innerHTML = `
  input[type="radio"]:checked + label::after {
    display: block;
  }
`;
document.head.appendChild(style);

const Title = styled.h3`
  margin: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RadioContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1rem 0;
`;

const RadioWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500};
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray};
  }
  
  /* Add a selected state style */
  &[data-selected="true"] {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.primary + '10'};
  }
`;

const Radio = styled.input`
  /* Hide the default radio button */
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSize.medium};
  font-weight: 500;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  position: relative;
  padding-left: 35px;
  user-select: none;
  width: 100%;
  
  /* Create custom radio button */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 2px;
    height: 22px;
    width: 22px;
    background-color: #fff;
    border: 2px solid ${({ theme }) => theme.colors.gray500};
    border-radius: 50%;
    
    @media (max-width: 850px) {
      height: 24px;
      width: 24px;
    }
  }
  
  /* Add the dot when checked */
  &::after {
    content: '';
    position: absolute;
    left: 7px;
    top: 9px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    display: none;
    
    @media (max-width: 850px) {
      left: 8px;
      top: 10px;
    }
  }
`;

const Example = styled.span`
  font-size: ${({ theme }) => theme.fontSize.small};
  font-weight: normal;
  color: ${({ theme }) => theme.colors.gray500};
  margin-top: 0.25rem;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
`;
